import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { WebsiteContentLang } from '../graphql/types';

dayjs.extend(utc);
dayjs.extend(timezone);

/** 业务时区：官网显示日期、定时发布都按美西时间 */
export const PACIFIC_TIMEZONE = 'America/Los_Angeles';
export const BEIJING_TIMEZONE = 'Asia/Shanghai';

/** 官网显示日期：按美西时间取日期（与官网一致） */
export const formatPacificDate = (iso: string | null | undefined): string =>
    iso ? dayjs(iso).tz(PACIFIC_TIMEZONE).format('YYYY-MM-DD') : '-';

/** 美西时间，如 2026-11-02 09:00 PT */
export const formatPacificDateTime = (iso: string | null | undefined): string =>
    iso ? `${dayjs(iso).tz(PACIFIC_TIMEZONE).format('YYYY-MM-DD HH:mm')} PT` : '-';

/** 北京时间对照，如 2026-11-03 00:00 */
export const formatBeijingDateTime = (iso: string | null | undefined): string =>
    iso ? dayjs(iso).tz(BEIJING_TIMEZONE).format('YYYY-MM-DD HH:mm') : '-';

/** 操作人所在地的本地时间 */
export const formatLocalDateTime = (iso: string | null | undefined): string =>
    iso ? dayjs(iso).format('YYYY-MM-DD HH:mm') : '-';

const SIZE_UNITS = ['B', 'KB', 'MB', 'GB'];

export const formatFileSize = (bytes: number | string | null | undefined): string => {
    let size = Number(bytes ?? 0);
    if (!Number.isFinite(size) || size <= 0) return '0 B';
    let unit = 0;
    while (size >= 1024 && unit < SIZE_UNITS.length - 1) {
        size /= 1024;
        unit += 1;
    }
    return `${unit === 0 ? size : size.toFixed(size >= 10 ? 0 : 1)} ${SIZE_UNITS[unit]}`;
};

/** 某语言的官网地址：英文无前缀，西语、中文在路径前加 /es、/zh */
export const localizedPublicUrl = (publicUrl: string, lang: WebsiteContentLang): string => {
    if (lang === 'EN') return publicUrl;
    try {
        const url = new URL(publicUrl);
        return `${url.origin}/${lang.toLowerCase()}${url.pathname}`;
    } catch {
        return publicUrl;
    }
};

/** 后端短码规则（WebsiteContentSlug）：8 位小写字母或数字 */
const SLUG_PATTERN = /^[a-z0-9]{8}$/;
/** 官网详情页路径：/news/<短码>、/es/news/<短码>、/zh/news/<短码>，可带域名、查询串和末尾斜杠 */
const NEWS_LINK_PATTERN = /^(?:[a-z][a-z0-9+.-]*:\/\/[^/\s]+)?(?:\/(?:es|zh))?\/news\/([a-z0-9]{8})\/?(?:[?#].*)?$/i;

/**
 * 搜索词归一：粘贴的官网详情链接取出短码，其余去掉首尾空格原样搜索。
 * 短码与标题由后端一并匹配（短码完全相等或标题包含）。
 */
export const normalizeSearchKeyword = (input: string): string => {
    const keyword = input.trim();
    const match = keyword.match(NEWS_LINK_PATTERN);
    if (match && SLUG_PATTERN.test(match[1].toLowerCase())) return match[1].toLowerCase();
    return keyword;
};
